import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { AuthApi } from "../../api/common/apiIndex";
import { toast } from "react-toastify";
import { useSearchParams, useNavigate } from "react-router-dom";
import { IoIosKey } from "react-icons/io";
import "./LoginForm.css";
import Loader from "../common/commonUtils/Loader";
import PublicShell from "../common/shell/PublicShell";
import useLoginCredentialsForm from "../common/userRegistration/useLoginCredentialsForm";
import PasswordField from "../common/userRegistration/PasswordField.js";
import PasswordStrength from "../common/userRegistration/PasswordStrength.js";

const PASSWORD_HELP = "Must include upper, lower, digit, symbol (8–128).";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [checking, setChecking] = useState(true);
  const [tokenStatus, setTokenStatus] = useState("CHECKING"); 
  const {
    formData,
    setField,
    markTouched,
    errors,
    shouldShow,
    strength,
    canSubmit,
    buildPayload,
    setShowErrors,
  } = useLoginCredentialsForm({ includeRole: false, includeEmail: false });

  useEffect(() => {
    if (!token) {
      setTokenStatus("INVALID");
      setChecking(false);
      return;
    }

    const validate = async () => {
      try {
        const res = await AuthApi.validateToken({ token });
        console.log("status token: ", res);
        if (res.code === "TOKEN_VALID" || res.status === "VALID") {
          setTokenStatus("VALID");
        } else {
          setTokenStatus(res.status || "INVALID");
        }
      } catch (err) {
        const code = err?.response?.data?.code;
        if (code === "TOKEN_EXPIRED") setTokenStatus("EXPIRED");
        else if (code === "TOKEN_USED") setTokenStatus("USED");
        else setTokenStatus("INVALID");
      } finally {
        setChecking(false);
      }
    };

    validate();
  }, [token]);


  const mutation = useMutation({
    mutationFn: ({ token, newPassword }) => AuthApi.resetPassword({ token, newPassword }),
    onSuccess: (msg) => {
      toast.success(msg || "Password reset successfully.");
      navigate("/login");
    },
    onError: (err) => {
      const msg = err?.response?.data || "Invalid or expired link.";
      toast.error(msg);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowErrors(true);
    if (!canSubmit) return;
    const payload = buildPayload();
    mutation.mutate({ token, newPassword: payload.password });
  };

  if (checking) {
    return (
      <PublicShell className="login-light">
        <Loader text="Validating link..." className="py-5" />
      </PublicShell>
    );
  }

  if (tokenStatus !== "VALID") {
    const isExpired = tokenStatus === "EXPIRED";
    const isUsed = tokenStatus === "USED";
    const title = isExpired
      ? "Link Expired"
      : isUsed
      ? "Link Already Used"
      : "Invalid Link";

    const message = isExpired
      ? "This password reset link has expired. Please request a new one."
      : isUsed
      ? "This link has already been used. Please request a new one."
      : "The password reset link is missing or invalid.";

    return (
      <PublicShell className="login-light">
        <div className="card shadow-sm border-0 rounded-4 w-100" style={{ maxWidth: 360 }}>
          <div className="card-body p-3 p-sm-4 text-center">
            <h1 className="h5 fw-semibold mb-3 text-danger">{title}</h1>
            <p className="text-muted small mb-3">{message}</p>
            <button
              onClick={() => navigate("/forgot-password")}
              className="btn btn-primary rounded-pill"
            >
              Request a new link
            </button>
          </div>
        </div>
      </PublicShell>
    );
  }

  return (
    <PublicShell className="login-light">
      <form
        onSubmit={handleSubmit}
        className="card shadow-sm border-0 rounded-4 w-100"
        style={{ maxWidth: 360 }}
      >
        <div className="card-body p-3 p-sm-4">
          <h1 className="h4 text-center fw-semibold mb-3">Reset Password</h1>
          <p className="text-muted small text-center mb-4">
            Enter your new password below.
          </p>

          <PasswordField
            label="New Password"
            name="password"
            value={formData.password}
            onChange={(event) => setField("password", event.target.value)}
            onBlur={() => markTouched("password")}
            placeholder="Enter password"
            autoComplete="new-password"
            disabled={mutation.isPending}
            invalid={shouldShow.password && errors.password}
            invalidText={PASSWORD_HELP}
          />

          <PasswordField
            label="Confirm Password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={(event) => setField("confirmPassword", event.target.value)}
            onBlur={() => markTouched("confirmPassword")}
            placeholder="Confirm password"
            autoComplete="new-password"
            disabled={mutation.isPending}
            invalid={shouldShow.confirm && errors.confirm}
            invalidText="Passwords must match."
          />

          {formData.password && <PasswordStrength strength={strength} />}

          <button
            type="submit"
            className="btn btn-primary w-100 rounded-3 d-flex align-items-center justify-content-center"
            disabled={mutation.isPending}
          >
            {mutation.isPending && (
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
            )}
            <IoIosKey className="me-2" />
            Reset Password
          </button>
        </div>
      </form>
    </PublicShell>
  );
}
